"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, FileText, Zap, Cpu, Globe, TrendingUp, ShoppingCart, SearchCheck, Compass, ArrowRight, CircleCheck as CheckCircle2, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const servicesList = [{
  title: "Google Business Profile Optimization",
  category: "Local SEO",
  description: "Maximize map pack rankings with meticulous profile completion, strategic category selection, geotagged assets, and ongoing local post scheduling.",
  icon: MapPin,
  link: "/LocalSEOPage",
  highlight: "Map Pack Top 3 Focus"
}, {
  title: "Local Citation Building & Audit",
  category: "Authority",
  description: "Audit and resolve NAP (Name, Address, Phone) inconsistencies across top-tier UK directories to establish robust regional search signals.",
  icon: Building2,
  link: "/LocalSEOPage",
  highlight: "100% Manual Submissions"
}, {
  title: "Review & Reputation Strategy",
  category: "Conversion",
  description: "Structured workflows that convert satisfied clients into genuine, high-impact Google reviews to elevate click-through rates and trust.",
  icon: Star,
  link: "/LocalSEOPage",
  highlight: "Trust Signal Boosting"
}, {
  title: "Local Schema & Structured Data",
  category: "Technical",
  description: "Deploy precision JSON-LD schema for LocalBusiness, geo-coordinates, service areas, and sameAs properties to clarify entity relationships.",
  icon: Code2,
  link: "/TechnicalSeoPage",
  highlight: "Rich Snippet Precision"
}, {
  title: "On-Page Geo-Targeting Alignment",
  category: "Content",
  description: "Align core website pages with user search intent, localized keyword clusters, and targeted landing page architecture built to convert.",
  icon: FileText,
  link: "/NationalSeoPage",
  highlight: "Intent-Based Content"
}, {
  title: "Technical SEO & Speed Audits",
  category: "Technical",
  description: "Deep crawl analysis, crawl budget management, indexation cleanup, and Core Web Vitals acceleration for optimal search health.",
  icon: Zap,
  link: "/TechnicalSeoPage",
  highlight: "Core Web Vitals Ready"
}, {
  title: "AI Search & GEO Optimization",
  category: "Modern SEO",
  description: "Position your consultancy across Google SGE, Perplexity, and AI search engines through structured entity authority and clear answers.",
  icon: Cpu,
  link: "/AISearchOptimisationPage",
  highlight: "AI-Ready Visibility"
}, {
  title: "Multi-Location SEO Expansion",
  category: "Scalability",
  description: "Structured architectural blueprints for multi-branch businesses aiming to dominate local search across multiple town and city hubs.",
  icon: Globe,
  link: "/LocalSEOPage",
  highlight: "Scalable Regional Hubs"
}, {
  title: "B2B SEO & Lead Generation",
  category: "B2B Growth",
  description: "Capture decision-makers searching for specialized high-value services with intent-driven B2B strategies and targeted conversion paths.",
  icon: TrendingUp,
  link: "/B2BSeoLeadGenerationPage",
  highlight: "High-Intent Lead Flow"
}, {
  title: "E-Commerce Localized Strategy",
  category: "E-Commerce",
  description: "Optimize category hierarchies, product schema, and localized inventory signals to attract ready-to-buy shoppers in your markets.",
  icon: ShoppingCart,
  link: "/EcommerceSeoPage",
  highlight: "Revenue-Driven Architecture"
}, {
  title: "Comprehensive 360° SEO Audits",
  category: "Diagnostics",
  description: "In-depth forensic audit dissecting technical flaws, toxic backlinks, and hidden content bottlenecks with an actionable execution plan.",
  icon: SearchCheck,
  link: "/SeoAuditsPage",
  highlight: "Actionable Roadmap"
}, {
  title: "International SEO Strategy",
  category: "Global Reach",
  description: "Hreflang implementation, ccTLD vs subfolder architecture, and cross-border content positioning for overseas business growth.",
  icon: Compass,
  link: "/InternationalSeoPage",
  highlight: "Cross-Border Scale"
}];
export default function Service() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicegridoptimized" data-nav="light" className="py-20 md:py-28 bg-background text-foreground transition-colors duration-300">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border-border">
            Hands-On Freelance Expertise
          </Badge>

          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Specialized SEO Consultancy Solutions
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            You work directly with me, Yassine Essaadi. No account managers, junior staff, or automated templates—just tailored search engineering designed for sustained revenue growth.
          </p>

          <div className="inline-flex items-center gap-2 pt-2 text-sm font-medium text-foreground bg-muted/60 px-4 py-2 rounded-full border border-border">
            <UserCheck className="size-4 text-primary shrink-0" />
            <span>Direct Consultant Access & Explicit Deliverables</span>
          </div>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesList.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} variants={cardVariants} data-index={i} className="h-full">
                <Card className="h-full flex flex-col justify-between overflow-hidden border border-border bg-card text-card-foreground shadow-sm hover:shadow-md hover:border-primary/40 transition-[box-shadow,border-color] duration-300 rounded-xl">
                  {/* Category Header */}
                  <div className="bg-primary text-primary-foreground px-6 py-5 flex items-center justify-between border-b border-primary/20">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary-foreground/10 text-primary-foreground shrink-0">
                        <IconComponent className="size-5" />
                      </div>
                      <span className="text-xs font-medium tracking-wide uppercase opacity-90">
                        {service.category}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-[11.5px] bg-primary-foreground/15 border-primary-foreground/30 text-primary-foreground font-normal">
                      {service.highlight}
                    </Badge>
                  </div>

                  {/* Body Content */}
                  <CardHeader className="pt-6 pb-2">
                    <CardTitle className="text-xl font-semibold text-foreground font-default leading-snug">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="flex-1 pb-6">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </CardDescription>

                    <ul className="mt-4 space-y-2 text-xs text-foreground/80">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>Tailored specifically for your market segment</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>Transparent reporting with zero jargon</span>
                      </li>
                    </ul>
                  </CardContent>

                  {/* CTA Footer */}
                  <CardFooter className="pt-0 pb-6">
                    <Link to={service.link} className="w-full">
                      <Button variant="outline" className="w-full justify-between group text-sm font-medium border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-[color,background-color,border-color]">
                        <span>Learn More</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Consultation Banner */}
        <div className="mt-16 text-center bg-muted/40 border border-border p-8 rounded-2xl max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-left space-y-1">
            <h3 className="text-lg md:text-xl font-semibold text-foreground font-default">
              Unsure which strategy fits your current growth stage?
            </h3>
            <p className="text-sm text-muted-foreground">
              Book a direct consultation to discuss your specific technical and local SEO requirements.
            </p>
          </div>
          <Link to="/ContactPage" className="shrink-0 w-full md:w-auto">
            <Button size="lg" className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6">
              Book a Free SEO Consultation
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}